import { useEffect, useRef } from "react";

import { trpc } from "@/lib/trpc";

export const shouldMarkBrandAssistantChatSeen = (input: {
  enabled: boolean;
  visibilityState: DocumentVisibilityState;
  assistantContentVersion: number;
  seenAssistantContentVersion: number;
}) =>
  input.enabled &&
  input.visibilityState === "visible" &&
  input.assistantContentVersion > input.seenAssistantContentVersion;

export function useMarkBrandAssistantChatSeen(input: {
  brandId: string;
  chat:
    | {
        id: string;
        assistantContentVersion: number;
        seenAssistantContentVersion: number;
      }
    | null
    | undefined;
  enabled?: boolean;
}) {
  const utils = trpc.useUtils();
  const requestedVersionsRef = useRef(new Map<string, number>());
  const { mutate: markSeen } = trpc.brandAssistantChat.markSeen.useMutation({
    onSuccess(data, variables) {
      utils.brandAssistantChat.get.setData(
        { brandId: variables.brandId, chatId: variables.chatId },
        (current) => (current?.chat ? { chat: { ...current.chat, ...data.chat } } : current),
      );
      void utils.brandAssistantChat.list.invalidate({ brandId: variables.brandId });
      void utils.brandAssistantChat.list.invalidate({ brandId: variables.brandId, limit: 3 });
    },
    onError(_error, variables) {
      const key = `${variables.brandId}:${variables.chatId}`;
      if (requestedVersionsRef.current.get(key) === variables.seenThroughVersion) {
        requestedVersionsRef.current.delete(key);
      }
    },
  });
  const { chat } = input;
  const enabled = input.enabled ?? true;

  useEffect(() => {
    if (!chat) {
      return;
    }

    const requestSeenUpdate = () => {
      if (
        !shouldMarkBrandAssistantChatSeen({
          enabled,
          visibilityState: document.visibilityState,
          assistantContentVersion: chat.assistantContentVersion,
          seenAssistantContentVersion: chat.seenAssistantContentVersion,
        })
      ) {
        return;
      }

      const key = `${input.brandId}:${chat.id}`;
      if ((requestedVersionsRef.current.get(key) ?? -1) >= chat.assistantContentVersion) {
        return;
      }

      requestedVersionsRef.current.set(key, chat.assistantContentVersion);
      markSeen({
        brandId: input.brandId,
        chatId: chat.id,
        seenThroughVersion: chat.assistantContentVersion,
      });
    };

    requestSeenUpdate();
    document.addEventListener("visibilitychange", requestSeenUpdate);
    return () => document.removeEventListener("visibilitychange", requestSeenUpdate);
  }, [chat, enabled, input.brandId, markSeen]);
}
